import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Centralized API client utility
 * Provides configured axios instances and helper methods
 * Eliminates duplicate header/auth logic across service clients
 */

// Service base URLs from environment
export const JMAP_GATEWAY_URL = (import.meta as any).env?.VITE_JMAP_GATEWAY_URL || 'http://localhost:8082';
export const TENANT_SERVICE_URL = (import.meta as any).env?.VITE_TENANT_SERVICE_URL || 'http://localhost:8081';

/**
 * Create an axios instance with authentication headers
 */
function createAuthenticatedInstance(baseURL: string, token: string): AxiosInstance {
  return axios.create({
    baseURL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Generic API client for authenticated requests
 */
export class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, token: string, config?: AxiosRequestConfig): Promise<T> {
    const instance = createAuthenticatedInstance(this.baseURL, token);
    const response: AxiosResponse<T> = await instance.get(endpoint, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, token: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const instance = createAuthenticatedInstance(this.baseURL, token);
    const response: AxiosResponse<T> = await instance.post(endpoint, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, token: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const instance = createAuthenticatedInstance(this.baseURL, token);
    const response: AxiosResponse<T> = await instance.put(endpoint, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, token: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const instance = createAuthenticatedInstance(this.baseURL, token);
    const response: AxiosResponse<T> = await instance.patch(endpoint, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = void>(endpoint: string, token: string, config?: AxiosRequestConfig): Promise<T> {
    const instance = createAuthenticatedInstance(this.baseURL, token);
    const response: AxiosResponse<T> = await instance.delete(endpoint, config);
    return response.data;
  }

  /**
   * GET request with 404 fallback to null (common pattern)
   */
  async getOrNull<T>(endpoint: string, token: string, config?: AxiosRequestConfig): Promise<T | null> {
    try {
      return await this.get<T>(endpoint, token, config);
    } catch (err: any) {
      if (err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }
}

/**
 * Pre-configured API clients for common services
 */
export const jmapGatewayClient = new ApiClient(JMAP_GATEWAY_URL);
export const tenantServiceClient = new ApiClient(TENANT_SERVICE_URL);

/**
 * Legacy compatibility - create headers object
 * Use ApiClient methods instead for new code
 */
export function createAuthHeaders(token: string) {
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}
